import React, { useMemo } from 'react';
import BrandLogo from '../components/BrandLogo';
import { detectRegion } from '../utils/countryDetection';

const REGION_INTRO = {
  us: 'This Privacy Policy applies to users in the United States, governed by applicable federal and state laws including the California Consumer Privacy Act as amended by CPRA.',
  eu: 'This Privacy Policy applies to users in the European Economic Area, governed by the General Data Protection Regulation (GDPR).',
  in: 'This Privacy Policy applies to users in India, governed by the Digital Personal Data Protection Act, 2023 (DPDP Act).',
  global: 'This Privacy Policy applies globally to all users of LoopVisitor, including users in the United States (governed by applicable state laws including the California Consumer Privacy Act as amended by CPRA), the European Economic Area (governed by the General Data Protection Regulation), India (governed by the Digital Personal Data Protection Act, 2023), and all other jurisdictions.',
};

const sectionTitle = {
  fontSize: 22,
  fontWeight: 700,
  marginTop: 40,
  marginBottom: 14,
  color: '#1E293B',
};

const subSectionTitle = {
  fontSize: 18,
  fontWeight: 600,
  marginTop: 28,
  marginBottom: 10,
  color: '#334155',
};

const paragraph = {
  lineHeight: 1.85,
  marginBottom: 16,
  color: '#374151',
  fontSize: 15,
};

const listStyle = {
  lineHeight: 1.85,
  marginBottom: 16,
  paddingLeft: 24,
  color: '#374151',
  fontSize: 15,
};

const listItem = {
  marginBottom: 8,
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  marginBottom: 20,
  fontSize: 14,
};

const thStyle = {
  textAlign: 'left',
  padding: '10px 14px',
  borderBottom: '2px solid #E2E8F0',
  background: '#F8FAFC',
  fontWeight: 600,
  color: '#1E293B',
};

const tdStyle = {
  padding: '10px 14px',
  borderBottom: '1px solid #E2E8F0',
  verticalAlign: 'top',
  color: '#374151',
};

function show(region, ...targets) {
  return region === 'global' || targets.includes(region);
}

export default function Privacy() {
  const region = useMemo(() => detectRegion(), []);

  return (
    <div style={{ maxWidth: 780, margin: '60px auto', padding: '0 24px 80px' }}>
      <div style={{ marginBottom: 24 }}>
        <BrandLogo className="landing-logo" />
      </div>

      <h1 style={{ fontSize: 30, fontWeight: 700, marginBottom: 8, color: '#0F172A' }}>Privacy Policy</h1>
      <p style={{ color: '#64748B', lineHeight: 1.8, marginBottom: 8 }}>
        <strong>Effective Date:</strong> June 13, 2026
      </p>
      <p style={{ color: '#64748B', lineHeight: 1.8, marginBottom: 24 }}>
        <strong>Last Updated:</strong> June 13, 2026
      </p>

      <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 8, padding: '16px 20px', marginBottom: 32 }}>
        <p style={{ ...paragraph, marginBottom: 0, color: '#0C4A6E', fontSize: 14 }}>
          {REGION_INTRO[region]}
        </p>
      </div>

      {/* ── 1. INTRODUCTION ── */}
      <h2 style={sectionTitle}>1. Introduction</h2>
      <p style={paragraph}>
        LoopVisitor ("<strong>LoopVisitor</strong>," "<strong>we</strong>," "<strong>our</strong>," or "<strong>us</strong>") operates the website at <strong>loopvisitor.com</strong> and the LoopVisitor platform (collectively, the "<strong>Service</strong>"). We are committed to protecting the privacy and security of personal data entrusted to us.
      </p>
      <p style={paragraph}>
        This Privacy Policy describes what personal data we collect, why we collect it, how we use and share it, how long we retain it, and the rights and choices available to you. By accessing or using our Service, you acknowledge that you have read and understood this Privacy Policy.
      </p>

      {/* ── 2. DEFINITIONS ── */}
      <h2 style={sectionTitle}>2. Definitions</h2>
      <ul style={listStyle}>
        <li style={listItem}><strong>"Business User"</strong> means any individual or entity that creates a LoopVisitor account to manage customer engagement for their business.</li>
        <li style={listItem}><strong>"End Customer"</strong> means any individual whose personal data is imported or entered into LoopVisitor by a Business User.</li>
        <li style={listItem}><strong>"Personal Data"</strong> means any information that identifies or can be used to identify a natural person, directly or indirectly.</li>
        <li style={listItem}><strong>"Processing"</strong> means any operation performed on Personal Data, including collection, storage, use, disclosure, or deletion.</li>
        {show(region, 'eu', 'in') && (
          <li style={listItem}>
            {show(region, 'in') && <><strong>"Data Fiduciary"</strong> (as defined under India's DPDP Act, 2023){show(region, 'eu') ? ' and ' : ' refers to the entity that determines the purposes and means of processing Personal Data.'}</>}
            {show(region, 'eu') && <><strong>"Data Controller"</strong> (as defined under GDPR) refers to the entity that determines the purposes and means of processing Personal Data.</>}
          </li>
        )}
        {show(region, 'eu', 'in') && (
          <li style={listItem}><strong>"Data Processor"</strong> means the entity that processes Personal Data on behalf of the Data Controller{show(region, 'in') ? ' / Data Fiduciary' : ''}.</li>
        )}
      </ul>

      {/* ── 3. ROLES AND RESPONSIBILITIES ── */}
      {show(region, 'eu', 'in') && (
        <>
          <h2 style={sectionTitle}>3. Our Role: Data Controller vs. Data Processor</h2>
          <p style={paragraph}>
            Understanding our role is important for determining how your data is handled:
          </p>
          <ul style={listStyle}>
            <li style={listItem}>
              <strong>Data Controller{show(region, 'in') ? ' / Data Fiduciary' : ''}:</strong> We act as the Data Controller for Personal Data we collect directly from Business Users — for example, account registration information, billing data, and support inquiries.
            </li>
            <li style={listItem}>
              <strong>Data Processor:</strong> When Business Users import or enter their End Customers' data into LoopVisitor (names, emails, phone numbers, visit history), <strong>the Business User is the Data Controller</strong> and LoopVisitor acts as a Data Processor. We process End Customer data solely on the Business User's instructions and in accordance with this policy.
            </li>
          </ul>
          <p style={paragraph}>
            Business Users are responsible for obtaining all necessary consents and providing appropriate privacy notices to their End Customers before importing their data into LoopVisitor.
          </p>
        </>
      )}

      {/* ── 4. INFORMATION WE COLLECT ── */}
      <h2 style={sectionTitle}>{show(region, 'eu', 'in') ? '4' : '3'}. Information We Collect</h2>

      <h3 style={subSectionTitle}>{show(region, 'eu', 'in') ? '4' : '3'}.1 Account and Registration Data</h3>
      <p style={paragraph}>When you create a LoopVisitor account, we collect:</p>
      <ul style={listStyle}>
        <li style={listItem}>Email address</li>
        <li style={listItem}>Password (stored in hashed form only; we never store or have access to your plaintext password)</li>
        <li style={listItem}>Account creation and verification timestamps</li>
      </ul>

      <h3 style={subSectionTitle}>{show(region, 'eu', 'in') ? '4' : '3'}.2 Business Profile Data</h3>
      <p style={paragraph}>To configure the Service for your business, we collect:</p>
      <ul style={listStyle}>
        <li style={listItem}>Business name, industry, and country</li>
        <li style={listItem}>Business email address and phone number</li>
        <li style={listItem}>Website URL, Google review link, and booking URL</li>
        <li style={listItem}>Brand/theme preferences (e.g., email theme color)</li>
        <li style={listItem}>WhatsApp Business API credentials (if you choose to connect WhatsApp)</li>
      </ul>

      <h3 style={subSectionTitle}>{show(region, 'eu', 'in') ? '4' : '3'}.3 End Customer Data (Imported by Business Users)</h3>
      <p style={paragraph}>Business Users may import or manually enter their customers' data, which may include:</p>
      <ul style={listStyle}>
        <li style={listItem}>Customer name</li>
        <li style={listItem}>Customer email address</li>
        <li style={listItem}>Customer phone number</li>
        <li style={listItem}>Last visit date</li>
        <li style={listItem}>Communication preferences and unsubscribe status</li>
      </ul>
      <p style={paragraph}>
        This data is uploaded via CSV file or manual entry. We process this data strictly on behalf of the Business User.
      </p>

      <h3 style={subSectionTitle}>{show(region, 'eu', 'in') ? '4' : '3'}.4 Messaging and Engagement Data</h3>
      <p style={paragraph}>When messages are sent through the Service, we collect:</p>
      <ul style={listStyle}>
        <li style={listItem}>Message logs (channel, category, delivery status, timestamps)</li>
        <li style={listItem}>Scheduled reminder information</li>
        <li style={listItem}>Link click data (timestamp, browser user agent, IP address — first click per link only)</li>
        <li style={listItem}>Engagement metrics (click/open totals, engagement scores)</li>
      </ul>

      <h3 style={subSectionTitle}>{show(region, 'eu', 'in') ? '4' : '3'}.5 Payment and Billing Data</h3>
      <p style={paragraph}>When you subscribe to a paid plan, we collect:</p>
      <ul style={listStyle}>
        <li style={listItem}>Razorpay customer ID and subscription ID</li>
        <li style={listItem}>Plan details and billing period</li>
      </ul>
      <p style={paragraph}>
        <strong>We do not store your credit card number, bank account details, or other payment instrument information.</strong> All payment processing is handled directly by Razorpay, which maintains PCI-DSS compliance. Please refer to{' '}
        <a href="https://razorpay.com/privacy/" target="_blank" rel="noopener noreferrer">Razorpay's Privacy Policy</a> for details on how they handle your payment data.
      </p>

      <h3 style={subSectionTitle}>{show(region, 'eu', 'in') ? '4' : '3'}.6 Support and Communication Data</h3>
      <p style={paragraph}>When you contact us or submit a support request, we collect:</p>
      <ul style={listStyle}>
        <li style={listItem}>Name, email address, and phone number</li>
        <li style={listItem}>Subject and message content</li>
        <li style={listItem}>Demo booking preferences and scheduling information</li>
      </ul>

      <h3 style={subSectionTitle}>{show(region, 'eu', 'in') ? '4' : '3'}.7 Technical and Automatically Collected Data</h3>
      <p style={paragraph}>We automatically collect certain information when you use our Service:</p>
      <ul style={listStyle}>
        <li style={listItem}><strong>IP Address:</strong> Used for rate limiting, security, and approximate country/region detection during business setup</li>
        <li style={listItem}><strong>Browser User Agent:</strong> Collected when tracked links are clicked to help measure engagement</li>
        <li style={listItem}><strong>Browser Timezone:</strong> Used to detect your likely country for displaying localized pricing</li>
        <li style={listItem}><strong>Local Storage:</strong> We store authentication tokens (JWT) in your browser's local storage to keep you signed in</li>
      </ul>

      {/* ── 5. HOW WE USE YOUR INFORMATION ── */}
      <h2 style={sectionTitle}>{show(region, 'eu', 'in') ? '5' : '4'}. How We Use Your Information</h2>
      <p style={paragraph}>We use the information we collect for the following purposes:</p>

      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Purpose</th>
            <th style={thStyle}>Data Used</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tdStyle}><strong>Provide and operate the Service</strong></td>
            <td style={tdStyle}>Account data, business profile, End Customer data, messaging data</td>
          </tr>
          <tr>
            <td style={tdStyle}><strong>Process payments and manage subscriptions</strong></td>
            <td style={tdStyle}>Billing data, Razorpay identifiers</td>
          </tr>
          <tr>
            <td style={tdStyle}><strong>Send transactional communications</strong></td>
            <td style={tdStyle}>Email address (verification, password reset, billing alerts)</td>
          </tr>
          <tr>
            <td style={tdStyle}><strong>Deliver messages on your behalf</strong></td>
            <td style={tdStyle}>End Customer email/phone, message content, templates</td>
          </tr>
          <tr>
            <td style={tdStyle}><strong>Track engagement and analytics</strong></td>
            <td style={tdStyle}>Link clicks, IP addresses, user agents, delivery status</td>
          </tr>
          <tr>
            <td style={tdStyle}><strong>Ensure security and prevent abuse</strong></td>
            <td style={tdStyle}>IP addresses, rate limiting data, authentication tokens</td>
          </tr>
          <tr>
            <td style={tdStyle}><strong>Provide customer support</strong></td>
            <td style={tdStyle}>Support ticket data, communication records</td>
          </tr>
          <tr>
            <td style={tdStyle}><strong>Comply with legal obligations</strong></td>
            <td style={tdStyle}>Any data required by applicable law</td>
          </tr>
          <tr>
            <td style={tdStyle}><strong>Improve and develop the Service</strong></td>
            <td style={tdStyle}>Aggregated usage patterns and engagement metrics</td>
          </tr>
        </tbody>
      </table>

      {/* ── 6. LEGAL BASES FOR PROCESSING ── */}
      <h2 style={sectionTitle}>{show(region, 'eu', 'in') ? '6' : '5'}. Legal Bases for Processing</h2>

      {show(region, 'eu') && (
        <>
          <h3 style={subSectionTitle}>Under GDPR (EEA Users)</h3>
          <p style={paragraph}>We rely on the following legal bases:</p>
          <ul style={listStyle}>
            <li style={listItem}><strong>Performance of a Contract (Art. 6(1)(b)):</strong> Processing necessary to provide the Service you have subscribed to</li>
            <li style={listItem}><strong>Legitimate Interests (Art. 6(1)(f)):</strong> Security, fraud prevention, service improvement, and analytics</li>
            <li style={listItem}><strong>Consent (Art. 6(1)(a)):</strong> Where we rely on consent, you may withdraw it at any time</li>
            <li style={listItem}><strong>Legal Obligation (Art. 6(1)(c)):</strong> Compliance with applicable laws and regulations</li>
          </ul>
        </>
      )}

      {show(region, 'us') && (
        <>
          <h3 style={subSectionTitle}>Under US State Privacy Laws (CCPA/CPRA)</h3>
          <p style={paragraph}>
            We do not "sell" or "share" (as defined under CCPA/CPRA) your Personal Data to third parties for monetary or other valuable consideration, nor for cross-context behavioral advertising.
          </p>
          <p style={paragraph}>
            We process Personal Data as necessary to provide the Service under our contractual relationship with you, and for our legitimate business purposes including security, fraud prevention, and service improvement.
          </p>
        </>
      )}

      {show(region, 'in') && (
        <>
          <h3 style={subSectionTitle}>Under India's DPDP Act, 2023</h3>
          <p style={paragraph}>We process personal data based on:</p>
          <ul style={listStyle}>
            <li style={listItem}><strong>Consent:</strong> We obtain your consent during account registration. You may withdraw consent at any time by contacting us or deleting your account, subject to any legal retention requirements.</li>
            <li style={listItem}><strong>Legitimate Uses:</strong> Processing necessary for the performance of our contract with you, compliance with law, or other legitimate uses specified under the Act.</li>
          </ul>
        </>
      )}

      {/* ── 7. DATA SHARING AND THIRD-PARTY SERVICES ── */}
      <h2 style={sectionTitle}>{show(region, 'eu', 'in') ? '7' : '6'}. Data Sharing and Third-Party Service Providers</h2>
      <p style={paragraph}>
        We do not sell your Personal Data. We share data only as necessary to provide the Service and as described below:
      </p>

      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Service Provider</th>
            <th style={thStyle}>Purpose</th>
            <th style={thStyle}>Data Shared</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tdStyle}><strong>Resend</strong></td>
            <td style={tdStyle}>Transactional and marketing email delivery</td>
            <td style={tdStyle}>Recipient email address, email content, delivery/open status</td>
          </tr>
          <tr>
            <td style={tdStyle}><strong>Brevo (Sendinblue)</strong></td>
            <td style={tdStyle}>SMS message delivery</td>
            <td style={tdStyle}>Recipient phone number, SMS content</td>
          </tr>
          <tr>
            <td style={tdStyle}><strong>Razorpay</strong></td>
            <td style={tdStyle}>Payment processing and subscription management</td>
            <td style={tdStyle}>Email, payment instrument details (collected directly by Razorpay)</td>
          </tr>
          <tr>
            <td style={tdStyle}><strong>Meta (Facebook / WhatsApp)</strong></td>
            <td style={tdStyle}>WhatsApp Business messaging (when connected by Business User)</td>
            <td style={tdStyle}>WhatsApp phone numbers, message content</td>
          </tr>
          <tr>
            <td style={tdStyle}><strong>Calendly</strong></td>
            <td style={tdStyle}>Demo scheduling</td>
            <td style={tdStyle}>Name, email, scheduling preferences</td>
          </tr>
          <tr>
            <td style={tdStyle}><strong>Aiven (PostgreSQL)</strong></td>
            <td style={tdStyle}>Database hosting</td>
            <td style={tdStyle}>All stored data (encrypted at rest and in transit)</td>
          </tr>
          <tr>
            <td style={tdStyle}><strong>IP Geolocation APIs</strong></td>
            <td style={tdStyle}>Country detection for pricing localization</td>
            <td style={tdStyle}>IP address (client-side only, during business setup)</td>
          </tr>
          <tr>
            <td style={tdStyle}><strong>Google Fonts</strong></td>
            <td style={tdStyle}>Font delivery</td>
            <td style={tdStyle}>IP address, browser information (collected by Google per their privacy policy)</td>
          </tr>
        </tbody>
      </table>

      <p style={paragraph}>We may also disclose Personal Data:</p>
      <ul style={listStyle}>
        <li style={listItem}>To comply with a legal obligation, court order, or government request</li>
        <li style={listItem}>To protect the rights, property, or safety of LoopVisitor, our users, or the public</li>
        <li style={listItem}>In connection with a merger, acquisition, or sale of assets (with prior notice to affected users)</li>
      </ul>

      {/* ── 8. INTERNATIONAL DATA TRANSFERS ── */}
      <h2 style={sectionTitle}>{show(region, 'eu', 'in') ? '8' : '7'}. International Data Transfers</h2>
      <p style={paragraph}>
        LoopVisitor operates globally, and your data may be transferred to and processed in countries other than your country of residence. Our infrastructure providers and third-party service providers may be located in the United States, European Union, India, or other jurisdictions.
      </p>
      <p style={paragraph}>
        Where Personal Data is transferred outside your jurisdiction, we ensure appropriate safeguards are in place, including:
      </p>
      <ul style={listStyle}>
        {show(region, 'eu') && (
          <li style={listItem}>Standard Contractual Clauses (SCCs) approved by the European Commission for transfers from the EEA</li>
        )}
        <li style={listItem}>Ensuring our service providers maintain adequate data protection standards</li>
        {show(region, 'in') && (
          <li style={listItem}>Compliance with the applicable provisions of India's DPDP Act regarding cross-border data transfers to permitted jurisdictions</li>
        )}
        {show(region, 'us') && (
          <li style={listItem}>Contractual obligations requiring our service providers to protect your data consistent with this Privacy Policy</li>
        )}
      </ul>

      {/* ── 9. DATA RETENTION ── */}
      <h2 style={sectionTitle}>{show(region, 'eu', 'in') ? '9' : '8'}. Data Retention</h2>
      <p style={paragraph}>We retain your data in accordance with the following principles:</p>
      <ul style={listStyle}>
        <li style={listItem}><strong>Account Data:</strong> Retained for as long as your account is active plus 90 days after deletion to allow for recovery, unless longer retention is required by law.</li>
        <li style={listItem}><strong>End Customer Data:</strong> Retained for as long as the Business User's account is active. Upon account deletion, End Customer data is deleted within 30 days.</li>
        <li style={listItem}><strong>Messaging Logs:</strong> Retained for up to 12 months for analytics and troubleshooting, then automatically purged or anonymized.</li>
        <li style={listItem}><strong>Link Click Data (IP, User Agent):</strong> Retained for up to 6 months, then deleted or anonymized.</li>
        <li style={listItem}><strong>Support Tickets:</strong> Retained for up to 24 months after resolution.</li>
        <li style={listItem}><strong>Billing Records:</strong> Retained as required by applicable tax and financial regulations (typically 5–7 years).</li>
      </ul>

      {/* ── 10. COOKIES AND TRACKING TECHNOLOGIES ── */}
      <h2 style={sectionTitle}>{show(region, 'eu', 'in') ? '10' : '9'}. Cookies and Tracking Technologies</h2>
      <p style={paragraph}>
        LoopVisitor does <strong>not</strong> set first-party cookies for analytics or advertising purposes. However, please note:
      </p>
      <ul style={listStyle}>
        <li style={listItem}>
          <strong>Local Storage:</strong> We use browser local storage to store your authentication token (JWT) to keep you logged in. This is essential for the Service to function and is not used for tracking.
        </li>
        <li style={listItem}>
          <strong>Facebook SDK:</strong> When the WhatsApp Business integration feature is enabled, the Meta/Facebook SDK may be loaded, which may set cookies and collect data according to{' '}
          <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer">Meta's Privacy Policy</a>.
        </li>
        <li style={listItem}>
          <strong>Google Fonts:</strong> Loading fonts from Google's servers may cause Google to collect certain technical data. See{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.
        </li>
        <li style={listItem}>
          <strong>Razorpay Checkout:</strong> When you make a payment, Razorpay's checkout script may set cookies as needed for payment processing and fraud detection.
        </li>
      </ul>

      {/* ── 11. DATA SECURITY ── */}
      <h2 style={sectionTitle}>{show(region, 'eu', 'in') ? '11' : '10'}. Data Security</h2>
      <p style={paragraph}>
        We implement commercially reasonable technical and organizational measures to protect your Personal Data, including:
      </p>
      <ul style={listStyle}>
        <li style={listItem}>Passwords are hashed using bcrypt before storage; we never store plaintext passwords</li>
        <li style={listItem}>All data in transit is encrypted using TLS/SSL</li>
        <li style={listItem}>Database connections are encrypted and access-controlled</li>
        <li style={listItem}>Authentication via signed JSON Web Tokens (JWT) with expiration</li>
        <li style={listItem}>Rate limiting on API endpoints to prevent abuse</li>
        <li style={listItem}>Security headers enforced via Helmet.js</li>
        <li style={listItem}>CORS restrictions to authorized origins only</li>
      </ul>
      <p style={paragraph}>
        While we strive to use commercially acceptable means to protect your data, no method of electronic transmission or storage is 100% secure. We cannot guarantee absolute security.
      </p>
      {show(region, 'in') && (
        <p style={paragraph}>
          In the event of a personal data breach, we will notify the Data Protection Board of India and affected Data Principals in accordance with the requirements of the DPDP Act, 2023.
        </p>
      )}
      {show(region, 'eu') && (
        <p style={paragraph}>
          In the event of a personal data breach likely to result in a risk to your rights and freedoms, we will notify the relevant Supervisory Authority within 72 hours and affected individuals without undue delay, in accordance with GDPR Articles 33 and 34.
        </p>
      )}

      {/* ── 12. YOUR RIGHTS AND CHOICES ── */}
      <h2 style={sectionTitle}>{show(region, 'eu', 'in') ? '12' : '11'}. Your Rights and Choices</h2>

      <h3 style={subSectionTitle}>Rights for All Users</h3>
      <p style={paragraph}>Regardless of your location, you have the right to:</p>
      <ul style={listStyle}>
        <li style={listItem}><strong>Access:</strong> Request a copy of the Personal Data we hold about you</li>
        <li style={listItem}><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
        <li style={listItem}><strong>Deletion:</strong> Request deletion of your account and associated Personal Data</li>
        <li style={listItem}><strong>Opt-out of Communications:</strong> Unsubscribe from marketing emails at any time</li>
      </ul>

      {show(region, 'eu') && (
        <>
          <h3 style={subSectionTitle}>Additional Rights under GDPR (EEA Residents)</h3>
          <ul style={listStyle}>
            <li style={listItem}><strong>Data Portability:</strong> Request your data in a structured, machine-readable format</li>
            <li style={listItem}><strong>Restriction of Processing:</strong> Request we limit processing in certain circumstances</li>
            <li style={listItem}><strong>Object to Processing:</strong> Object to processing based on legitimate interests</li>
            <li style={listItem}><strong>Withdraw Consent:</strong> Where processing is based on consent, withdraw it at any time</li>
            <li style={listItem}><strong>Lodge a Complaint:</strong> File a complaint with your local Data Protection Authority</li>
          </ul>
        </>
      )}

      {show(region, 'us') && (
        <>
          <h3 style={subSectionTitle}>Your Rights under CCPA/CPRA (California Residents)</h3>
          <ul style={listStyle}>
            <li style={listItem}><strong>Right to Know:</strong> Request details about the categories and specific pieces of Personal Data collected about you in the preceding 12 months</li>
            <li style={listItem}><strong>Right to Delete:</strong> Request deletion of your Personal Data, subject to certain exceptions</li>
            <li style={listItem}><strong>Right to Correct:</strong> Request correction of inaccurate Personal Data</li>
            <li style={listItem}><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights</li>
            <li style={listItem}><strong>Authorized Agent:</strong> You may designate an authorized agent to submit requests on your behalf, subject to verification</li>
            <li style={listItem}>We do not sell or share Personal Data as defined under CCPA/CPRA</li>
            <li style={listItem}>We do not use or disclose Sensitive Personal Information for purposes other than those permitted under CCPA/CPRA</li>
          </ul>
          <p style={paragraph}>
            To submit a verified consumer request, please email us at <strong>support@loopvisitor.com</strong>. We will verify your identity before fulfilling your request and respond within 45 days (with a possible 45-day extension if reasonably necessary).
          </p>
        </>
      )}

      {show(region, 'in') && (
        <>
          <h3 style={subSectionTitle}>Your Rights under India's DPDP Act, 2023</h3>
          <ul style={listStyle}>
            <li style={listItem}><strong>Right to Access:</strong> Obtain a summary of your Personal Data and the processing activities carried out on it</li>
            <li style={listItem}><strong>Right to Correction and Erasure:</strong> Request correction of inaccurate data or erasure of data that is no longer necessary for the purpose it was collected</li>
            <li style={listItem}><strong>Right to Grievance Redressal:</strong> Lodge a complaint with our Grievance Officer (details below) and, if unresolved, with the Data Protection Board of India</li>
            <li style={listItem}><strong>Right to Nominate:</strong> Nominate an individual to exercise your rights in the event of your death or incapacity</li>
            <li style={listItem}><strong>Right to Withdraw Consent:</strong> Withdraw your consent at any time, with the understanding that withdrawal will not affect the lawfulness of processing carried out prior to withdrawal</li>
          </ul>
          <p style={paragraph}>
            <strong>Your Duties as a Data Principal:</strong> Under the DPDP Act, you have a duty to provide accurate information, not to file false or frivolous complaints, and to furnish only authentic information when exercising your rights.
          </p>
        </>
      )}

      <p style={paragraph}>
        To exercise any of these rights, please contact us at <strong>support@loopvisitor.com</strong>. We will respond to verified requests within 30 days (or within the timeframe required by applicable law).
      </p>

      {/* ── 13. END CUSTOMER RIGHTS ── */}
      <h2 style={sectionTitle}>{show(region, 'eu', 'in') ? '13' : '12'}. Rights of End Customers</h2>
      <p style={paragraph}>
        If you are an End Customer whose data has been imported into LoopVisitor by a business:
      </p>
      <ul style={listStyle}>
        <li style={listItem}>
          <strong>Unsubscribe:</strong> Every email, SMS, and WhatsApp message sent through LoopVisitor contains an unsubscribe link. You can opt out of further communications from that business at any time.
        </li>
        <li style={listItem}>
          <strong>Data Requests:</strong> Since the Business User is the {show(region, 'eu', 'in') ? 'Data Controller' : 'party responsible'} for your data, please contact the business directly for access, correction, or deletion requests. If you are unable to reach the business, you may contact us at support@loopvisitor.com and we will assist where possible.
        </li>
        <li style={listItem}>
          <strong>Complaints:</strong> If you believe your data is being misused, you may contact us or file a complaint with the appropriate data protection authority in your jurisdiction.
        </li>
      </ul>

      {/* ── 14. CHILDREN'S PRIVACY ── */}
      <h2 style={sectionTitle}>{show(region, 'eu', 'in') ? '14' : '13'}. Children's Privacy</h2>
      <p style={paragraph}>
        LoopVisitor is designed for use by businesses and is not directed at individuals under the age of 18. We do not knowingly collect Personal Data from children{show(region, 'us') ? ' under 16 (or 13 under COPPA)' : ''}. If you are a parent or guardian and believe your child has provided Personal Data to us, please contact us at support@loopvisitor.com and we will promptly delete such data.
      </p>

      {/* ── 15. BUSINESS USER OBLIGATIONS ── */}
      <h2 style={sectionTitle}>{show(region, 'eu', 'in') ? '15' : '14'}. Business User Data Protection Obligations</h2>
      <p style={paragraph}>
        As a Business User, you acknowledge and agree that:
      </p>
      <ul style={listStyle}>
        <li style={listItem}>You are the {show(region, 'eu', 'in') ? 'Data Controller (or Data Fiduciary under Indian law)' : 'party responsible'} for all End Customer data you import into LoopVisitor</li>
        <li style={listItem}>You have obtained all necessary consents from your End Customers before importing their data, in compliance with applicable privacy laws</li>
        <li style={listItem}>You will provide your End Customers with appropriate privacy notices disclosing your use of LoopVisitor</li>
        <li style={listItem}>You will comply with all applicable anti-spam laws{show(region, 'us') ? ' (including CAN-SPAM Act and TCPA)' : ''}{show(region, 'eu') ? ' (including GDPR and ePrivacy Directive)' : ''}{show(region, 'in') ? " (including India's Telecom Commercial Communications Customer Preference Regulations)" : ''} when sending messages through LoopVisitor</li>
        <li style={listItem}>You will honor all unsubscribe requests promptly</li>
        <li style={listItem}>You will not import data of individuals who have not consented to receiving communications</li>
      </ul>

      {/* ── 16. CHANGES TO THIS POLICY ── */}
      <h2 style={sectionTitle}>{show(region, 'eu', 'in') ? '16' : '15'}. Changes to This Privacy Policy</h2>
      <p style={paragraph}>
        We may update this Privacy Policy from time to time. We will notify you of material changes by:
      </p>
      <ul style={listStyle}>
        <li style={listItem}>Posting the updated policy on our website with a revised "Last Updated" date</li>
        <li style={listItem}>Sending an email notification to registered Business Users for significant changes</li>
      </ul>
      <p style={paragraph}>
        Your continued use of the Service after the effective date of changes constitutes your acceptance of the updated policy.
      </p>

      {/* ── 17. CONTACT US ── */}
      <h2 style={sectionTitle}>{show(region, 'eu', 'in') ? '17' : '16'}. Contact Us</h2>
      <p style={paragraph}>
        If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
      </p>
      <ul style={listStyle}>
        <li style={listItem}><strong>Email:</strong> support@loopvisitor.com</li>
        <li style={listItem}><strong>Website:</strong> loopvisitor.com</li>
      </ul>

      {show(region, 'in') && (
        <>
          <h3 style={subSectionTitle}>Grievance Officer (for Indian Users under DPDP Act, 2023)</h3>
          <p style={paragraph}>
            In accordance with the Digital Personal Data Protection Act, 2023, we have designated a Grievance Officer to address your concerns:
          </p>
          <ul style={listStyle}>
            <li style={listItem}><strong>Name:</strong> Grievance Officer, LoopVisitor</li>
            <li style={listItem}><strong>Email:</strong> support@loopvisitor.com</li>
            <li style={listItem}><strong>Response Time:</strong> We will acknowledge your grievance within 48 hours and resolve it within 30 days of receipt.</li>
          </ul>
        </>
      )}

      {show(region, 'eu') && (
        <>
          <h3 style={subSectionTitle}>Data Protection Inquiries (for EEA Users)</h3>
          <p style={paragraph}>
            If you are in the EEA and have concerns about our data processing practices, you may contact us at support@loopvisitor.com. You also have the right to lodge a complaint with your local Supervisory Authority (Data Protection Authority).
          </p>
        </>
      )}

      {show(region, 'us') && (
        <>
          <h3 style={subSectionTitle}>Privacy Inquiries (for US Users)</h3>
          <p style={paragraph}>
            If you are a California resident and wish to exercise your rights under CCPA/CPRA, or if you are a resident of another US state with applicable privacy legislation, please email us at support@loopvisitor.com with "Privacy Request" in the subject line. You may also contact the California Attorney General's office if you believe your privacy rights have been violated.
          </p>
        </>
      )}

      <div style={{ marginTop: 40, padding: '16px 20px', background: '#F8FAFC', borderRadius: 8, border: '1px solid #E2E8F0' }}>
        <p style={{ ...paragraph, marginBottom: 0, fontSize: 13, color: '#64748B' }}>
          {region === 'in' && 'If you are unable to resolve your concern with our Grievance Officer, you may lodge a complaint with the Data Protection Board of India.'}
          {region === 'eu' && 'If you are unable to resolve your concern with us directly, you have the right to lodge a complaint with your local EU/EEA Data Protection Authority (Supervisory Authority).'}
          {region === 'us' && 'If you are unable to resolve your concern with us directly, California residents may contact the California Attorney General. Residents of other US states may contact their respective state attorney general.'}
          {region === 'global' && 'If you are unable to resolve your concern with us directly, you may have the right to lodge a complaint with the Data Protection Board of India, your local EU Data Protection Authority, or the California Attorney General, depending on your jurisdiction.'}
        </p>
      </div>
    </div>
  );
}
